import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { useSnackbar } from 'notistack';
import { useStore } from '@store/GlobalStore';
import { QuoteSetupPresetGetResponse } from '@lio/api-types';
import { OfferingWritingCompanyItem } from '@models/productOffering';
import { Box, Typography } from '@mui/material';
import { PanelBase, PanelBaseType } from '@pages/NewQuote/PanelBase/PanelBase';
import { ITogglePanel } from '@pages/NewQuote/TogglePanel';
import { useProductLabels } from '@services/productLabel';
import { useCurrentUserRole } from '@services/user';
import { useWritingCompany } from '@services/writingCompany/useWritingCompany';
import { Scope, useNewQuoteStore } from '@store/NewQuote';
import { ToastType } from '@components/toast';
import {
  InsuranceProductSelection,
  PolicyDateSelection,
  AgencySelection,
  WritingCompanySelection,
} from '@pages/NewQuote/scope/inputSelection';
import { RenewalDetailsSelection } from '@pages/NewQuote/scope/inputSelection/RenewalDetailsSelection';
import { EventDateRangeSelection } from '@pages/NewQuote/scope/inputSelection/EventDateRangeSelection';
import { ProductOfferingId } from '@models/newQuote';

interface IScopeOpenPanel extends ITogglePanel {
  onFieldChange: (value: boolean) => void;
  quotePreset?: QuoteSetupPresetGetResponse;
  // Renewal mode props (optional; only provided when in renewal mode)
  isRenewalMode?: boolean;
  existingPolicyNumber?: string;
  setExistingPolicyNumber?: (v: string) => void;
  renewalTermToBind?: string;
  setRenewalTermToBind?: (v: string) => void;
}

export const ScopeOpenPanel = ({
  onClose,
  onFieldChange,
  quotePreset,
  isRenewalMode = false,
  existingPolicyNumber = '',
  setExistingPolicyNumber,
  renewalTermToBind = '',
  setRenewalTermToBind,
}: IScopeOpenPanel) => {
  const { scope, updateScopeFields, resetNextScopePanels, isFormLoading } = useNewQuoteStore();
  const {
    brokerageAgency,
    brokerageProducer,
    productOffering,
    insuranceProductId,
    productOption,
    productLabel,
    effectiveDate,
    writingCompany,
    expirationDate,
    isSpecialEvent,
  } = scope;
  const [currentUser, userAuth] = useStore((state) => [state.currentUser, state.userAuth], shallow);
  const { isInternalUser, isCustomerUser, isBroker } = userAuth;
  const { currentUserRole } = useCurrentUserRole();
  const [hasEffectiveDateError, setHasEffectiveDateError] = useState(false);
  const { productLabels } = useProductLabels(insuranceProductId);
  const { writingCompanies } = useWritingCompany(isInternalUser, productOffering?.value);
  const hasQuotePreset = quotePreset;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const defaultWritingCompany = writingCompanies?.items?.find((p: OfferingWritingCompanyItem) =>
    p.isDefault ? p.writingCompany : undefined,
  );

  const initialFields: Scope = {
    brokerageAgency,
    brokerageProducer,
    productOffering,
    insuranceProductId,
    productOption,
    productLabel,
    effectiveDate,
    expirationDate,
    writingCompany,
    isSpecialEvent,
  };

  useEffect(() => {
    if (!isInternalUser && !isCustomerUser) {
      if (currentUserRole) {
        const currentProducer = currentUserRole.brokerageProducer;
        if (!currentProducer) {
          console.error('Cannot find brokerageId for external user', { currentUserRole });
          return;
        }
        updateScopeFields({
          ...initialFields,
          brokerageAgency: {
            value: currentProducer.brokerageId,
            label: '',
          },
          brokerageProducer: {
            value: currentProducer.id,
            label: '',
          },
        });
      }
    }
    // eslint-disable-next-line
  }, [isInternalUser, currentUserRole]);

  function updatePanelsAndFields(fields: Record<string, unknown>) {
    updateScopeFields(fields);
    onFieldChange(true);
    resetNextScopePanels();
  }

  useEffect(() => {
    if (!quotePreset) {
      return;
    }

    if (isBroker) {
      const brokerageId = currentUser?.brokerageUsers[0]?.brokerageId;
      if (brokerageId !== quotePreset.brokerage.id) {
        enqueueSnackbar('This URL preset is invalid.', {
          variant: ToastType.Info,
        });
        navigate('/new-quote');
        return;
      }
    }

    updatePanelsAndFields({
      brokerageAgency: { value: quotePreset.brokerage.id, label: quotePreset.brokerage.name },
      brokerageProducer: {
        value: quotePreset.brokerageProducer?.id,
        label: `${quotePreset.brokerageProducer?.user.firstName}  ${quotePreset.brokerageProducer?.user.lastName}`,
      },
      productOffering: quotePreset.offering
        ? { value: quotePreset.offering.id, label: quotePreset.offering?.label }
        : null,
      insuranceProductId: quotePreset.offering.insuranceProduct.id,
      productOption: quotePreset?.productOption
        ? {
            value: quotePreset.productOption.id,
            id: quotePreset.productOption.id,
            label: quotePreset.productOption.name,
          }
        : null,
      writingCompany: quotePreset?.writingCompany
        ? { id: quotePreset.writingCompany.id, name: quotePreset.writingCompany.name }
        : null,
      effectiveDate: null,
      expirationDate: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotePreset]);

  function handleAutocompleteClear(step: number) {
    const fieldsWithReset = getResetFieldValues(step);
    updatePanelsAndFields(fieldsWithReset);
  }

  function getResetFieldValues(step: number, fields: Record<string, unknown> = initialFields) {
    return {
      brokerageAgency: step < 1 ? undefined : fields.brokerageAgency,
      brokerageProducer: step < 2 ? undefined : fields.brokerageProducer,
      productOffering: step < 3 ? undefined : fields.productOffering,
      insuranceProductId: step < 3 ? undefined : fields.insuranceProductId,
      productOption: step < 4 ? undefined : fields.productOption,
      productLabel: step < 4 ? undefined : fields.productLabel,
      writingCompany: step < 5 ? defaultWritingCompany?.writingCompany : fields.writingCompany,
      effectiveDate: null,
      expirationDate: null,
    };
  }

  const handleBrokerageChange = (selectedValue: { id: string; name: string } | null | undefined) => {
    const fields = selectedValue
      ? {
          ...initialFields,
          brokerageAgency: {
            value: selectedValue.id,
            label: selectedValue.name,
          },
        }
      : initialFields;
    const fieldsWithReset = getResetFieldValues(1, fields);
    updatePanelsAndFields(fieldsWithReset);
  };

  const handleBrokerageProducerChange = (
    selectedValue: {
      brokerage?: { id: string; name: string };
      brokerageProducers?: Array<{ id: string }>;
      user: { firstName: string; lastName: string };
    } | null,
  ) => {
    const fields = selectedValue
      ? {
          ...initialFields,
          brokerageAgency: selectedValue?.brokerage
            ? {
                value: selectedValue.brokerage.id,
                label: selectedValue.brokerage.name,
              }
            : initialFields.brokerageAgency,
          brokerageProducer: {
            value: selectedValue.brokerageProducers?.[0]?.id,
            label: `${selectedValue.user.firstName} ${selectedValue.user.lastName}`,
          },
        }
      : initialFields;
    const fieldsWithReset = getResetFieldValues(2, fields);
    updatePanelsAndFields(fieldsWithReset);
  };

  const handleProductOfferingChange = (
    selectedValue: { id: string; label?: string; insuranceProduct: { id: string; name: string } } | null,
  ) => {
    const fields = selectedValue
      ? {
          ...initialFields,
          insuranceProductId: selectedValue?.insuranceProduct?.id,
          productOffering: {
            value: selectedValue.id,
            label: selectedValue?.label || selectedValue?.insuranceProduct.name || '',
          },
        }
      : initialFields;
    const fieldsWithReset = getResetFieldValues(3, fields);
    updatePanelsAndFields(fieldsWithReset);
  };

  const handleProductOptionChange = (selectedValue: { id: string; name: string } | null) => {
    const fields = selectedValue
      ? {
          ...initialFields,
          productOption: selectedValue,
        }
      : initialFields;
    const fieldsWithReset = getResetFieldValues(4, fields);

    updatePanelsAndFields(fieldsWithReset);
  };

  const handleProductLabelChange = (selectedValue: { id: string; name: string } | null) => {
    const fields = selectedValue
      ? {
          ...initialFields,
          productLabel: selectedValue,
        }
      : initialFields;
    const fieldsWithReset = getResetFieldValues(4, fields);
    updatePanelsAndFields(fieldsWithReset);
  };

  const handleWritingCompanyChange = (selectedValue: { id: string; name: string } | null) => {
    const fields = selectedValue
      ? {
          ...initialFields,
          writingCompany: selectedValue,
        }
      : initialFields;
    updatePanelsAndFields(fields);
  };

  const handleSelectedDateChange = (date: Date | null, context: { validationError: string | null }) => {
    setHasEffectiveDateError(context.validationError !== null);
    updatePanelsAndFields({ ...initialFields, effectiveDate: date });
  };

  const handleDateRangeChange = (
    effectiveDate: Date | null,
    expirationDate: Date | null,
    context: { validationError: string | null },
  ) => {
    setHasEffectiveDateError(context.validationError !== null);

    updatePanelsAndFields({ ...initialFields, effectiveDate, expirationDate });
  };
  const isHOAorCOA = [ProductOfferingId.CondoOwnersAssociation, ProductOfferingId.HomeOwnersAssociation].includes(
    productOffering?.value ?? ('' as any),
  );

  const shouldShowProductLabel = isInternalUser && (!isHOAorCOA || (isHOAorCOA && isRenewalMode));
  const isProductLabelFieldValid = !productLabels.length || !shouldShowProductLabel || productLabel;

  const isDateFieldValid = !!effectiveDate && !hasEffectiveDateError;
  const isExistingPolicyValid = !isRenewalMode || (!!existingPolicyNumber && /^\d+$/.test(existingPolicyNumber));
  const renewalTermInt = parseInt(renewalTermToBind, 10);
  const isRenewalTermValid = !isRenewalMode || (Number.isInteger(renewalTermInt) && renewalTermInt > 0);

  let isFormValid = !!(
    (hasQuotePreset && isDateFieldValid) ||
    (isProductLabelFieldValid && isDateFieldValid && brokerageProducer)
  );

  if (isRenewalMode) {
    isFormValid = !!(isFormValid && isExistingPolicyValid && isRenewalTermValid);
  }

  if (isSpecialEvent) {
    const isSpecialEventsValid =
      isSpecialEvent && !!productOption?.id && effectiveDate && expirationDate && !hasEffectiveDateError;
    isFormValid = !!isSpecialEventsValid;
  }

  const handleConfirm = () => {
    onClose();
  };

  return (
    <PanelBase
      name='Scope'
      step='1 of 4'
      type={PanelBaseType.Open}
      confirmButtonProps={{
        children: `Continue to ${isCustomerUser ? 'Customer' : 'Named Insured'}`,
        disabled: !isFormValid || isFormLoading,
        onClick: handleConfirm,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        {!hasQuotePreset && isInternalUser && (
          <AgencySelection
            handleAutocompleteClear={handleAutocompleteClear}
            handleBrokerageChange={handleBrokerageChange}
            handleBrokerageProducerChange={handleBrokerageProducerChange}
          />
        )}
        {hasQuotePreset ? (
          <Box
            sx={{
              textAlign: 'center',
            }}
          >
            <Typography
              variant='h4'
              sx={{
                mt: '10px',
              }}
            >
              Insurance Product
            </Typography>
            <Typography
              variant='body1'
              sx={{
                mt: '10px',
              }}
            >
              {quotePreset.offering?.label ?? quotePreset.offering.insuranceProduct.name}
            </Typography>

            {!hasQuotePreset.productOption && (
              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <InsuranceProductSelection
                  quotePresetSelectedInsuranceProductId={quotePreset.offering.insuranceProduct.id}
                  handleProductOptionChange={handleProductOptionChange}
                  handleProductLabelChange={handleProductLabelChange}
                  isRenewalMode={isRenewalMode}
                />
              </Box>
            )}
          </Box>
        ) : (
          <>
            <InsuranceProductSelection
              handleProductOfferingChange={handleProductOfferingChange}
              handleProductOptionChange={handleProductOptionChange}
              handleProductLabelChange={handleProductLabelChange}
              isRenewalMode={isRenewalMode}
            />
            <WritingCompanySelection handleWritingCompanyChange={handleWritingCompanyChange} />
            {isRenewalMode && (
              <RenewalDetailsSelection
                existingPolicyNumber={existingPolicyNumber}
                onExistingPolicyNumberChange={(v) => setExistingPolicyNumber && setExistingPolicyNumber(v)}
                renewalTermToBind={renewalTermToBind}
                onRenewalTermToBindChange={(v) => setRenewalTermToBind && setRenewalTermToBind(v)}
              />
            )}
          </>
        )}
        {isSpecialEvent ? (
          <EventDateRangeSelection quotePreset={quotePreset} handleSelectedDateChange={handleDateRangeChange} />
        ) : (
          <PolicyDateSelection quotePreset={quotePreset} handleSelectedDateChange={handleSelectedDateChange} />
        )}
      </Box>
    </PanelBase>
  );
};
